/**
 * Module-level ref for triggering action shortcuts from outside ChatPanel.
 *
 * ChatPanel populates this on mount; ActionsSection calls it for spell and weapon actions.
 * Same pattern as handleSendMessageRef used for deferred sends.
 */

import type { ActionCard } from '@danda/shared';

export interface ItemTriggerData {
  itemName: string;
  itemId?: string;
  effectType: string;
  spells?: Array<{ spellName: string; charges: number; target_audience?: string }>;
  abilities?: Array<{ name: string; trigger: string; charges?: number }>;
  charges?: number;
}

export interface ActionShortcutTrigger {
  triggerSpellShortcut: (spellName: string, spellLevel: number, freeCast?: boolean) => void;
  triggerWeaponAttackShortcut: (weaponName: string) => void;
  triggerAbilityShortcut: (
    abilityName: string,
    targetType?: 'self_only' | 'targeted',
    runtime?: { poolRemaining?: number }
  ) => void;
  triggerItemShortcut: (data: ItemTriggerData) => void;
  /**
   * Card-aware shortcut — prefers this over the legacy name-based shortcuts
   * when a card is available (Actions panel click). Dispatches via
   * `lib/intent/build-from-card.ts` so the resulting Intent carries
   * `flurry_of_blows`, `mode_id`, `metamagic`, etc. exactly as the engine
   * expects.
   */
  triggerCardShortcut: (card: ActionCard, runtime?: { poolRemaining?: number }) => void;
}

export const actionTriggerRef: {
  current: ActionShortcutTrigger | null;
} = { current: null };
